import { useRegisterWorkforceIntegration } from "@/modules/workforce-integrations/composables/workforce-integration"

import {
  CreateWorkforceIntegrationAuthSessionResponse,
  EMIVendorEnum,
  WorkforceIntegration,
  WorkforceIntegrationApiCreateWorkforceIntegrationAuthSessionRequest
} from "@vitable/asclepius-api-client-ts"
import useAsclepius, { AsclepiusApiResponseError } from "@common/composables/api/asclepius"
import { computed, isRef, ref, Ref } from "vue"

const HRIS_INTEGRATION_ALREADY_EXISTS_ERROR_CODE = "hris_integration_already_exists"

export function isHrisIntegrationAlreadyExistsError(error: unknown): error is AsclepiusApiResponseError {
  return error instanceof AsclepiusApiResponseError && error.appErrorCode === HRIS_INTEGRATION_ALREADY_EXISTS_ERROR_CODE
}

function useCreateFinchAuthSession() {
  const { getWorkforceIntegrationApi, callWithResponse, callResult } = useAsclepius()
  const session_id: Ref<string | null> = ref(null)
  const connect_url: Ref<string | null> = ref(null)

  async function createFinchAuthSession(request: WorkforceIntegrationApiCreateWorkforceIntegrationAuthSessionRequest) {
    const response: CreateWorkforceIntegrationAuthSessionResponse = await callWithResponse(() =>
      getWorkforceIntegrationApi().createWorkforceIntegrationAuthSession(request)
    )

    session_id.value = response.session_id
    connect_url.value = response.connect_url
  }

  return {
    createFinchAuthSession,
    createFinchAuthSessionResult: callResult,
    session_id,
    connect_url
  }
}

export function useFinchConnect(companyId: string | Ref<string> | (() => string)) {
  const { registerIntegration, registerIntegrationResult, integration } = useRegisterWorkforceIntegration()
  const { createFinchAuthSession, createFinchAuthSessionResult, session_id, connect_url } = useCreateFinchAuthSession()

  const resolveCompanyId = (): string =>
    typeof companyId === "function" ? companyId() : isRef(companyId) ? companyId.value : companyId

  async function startConnection() {
    const resolvedCompanyId = resolveCompanyId()
    if (!resolvedCompanyId) return

    await createFinchAuthSession({
      companyId: resolvedCompanyId,
      createWorkforceIntegrationAuthSessionRequest: {
        integration_id: undefined,
        vendor: "Finch"
      }
    })

    location.href = connect_url.value!
  }

  async function startReconnection(integration: WorkforceIntegration) {
    const resolvedCompanyId = resolveCompanyId()
    if (!resolvedCompanyId) return

    await createFinchAuthSession({
      companyId: resolvedCompanyId,
      createWorkforceIntegrationAuthSessionRequest: {
        integration_id: integration.id,
        vendor: "Finch"
      }
    })

    location.href = connect_url.value!
  }

  async function connect(finchAccessCode: string, integrationId: string | null = null) {
    const resolvedCompanyId = resolveCompanyId()
    if (!resolvedCompanyId) return

    await registerIntegration({
      companyId: resolvedCompanyId,
      registerWorkforceIntegrationRequest: {
        vendor: EMIVendorEnum.Finch,
        access_code: finchAccessCode,
        integration_id: integrationId
      }
    })
  }

  const isLoading = computed(
    () => createFinchAuthSessionResult.value.isLoading() || registerIntegrationResult.value.isLoading()
  )
  const isError = computed(
    () => createFinchAuthSessionResult.value.isFailure() || registerIntegrationResult.value.isFailure()
  )
  const errorMessage = computed(
    () => createFinchAuthSessionResult.value.getErrorMessage() || registerIntegrationResult.value.getErrorMessage()
  )

  return {
    connect,
    startConnection,
    startReconnection,
    finchConnectResult: registerIntegrationResult,
    integration,
    isLoading,
    isError,
    errorMessage
  }
}
